/**
 * RFIReferencesSection Component
 * References (drawings, specs, photos) management for RFIs
 * BuilderBel Construction Project Management System
 */

import { FileText, Image, Plus, Trash2, X, File, ExternalLink } from 'lucide-react';
import React, { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { useAddRFIReferenceMutation, useRemoveRFIReferenceMutation } from '../../services/rfiApi';
import type { RFI, RFIReference, RFIReferenceType } from '../../types/rfi';
import { Input, Dropdown, TextArea } from '../forms';

interface RFIReferencesSectionProps {
  projectId: string;
  rfi: RFI;
  canEdit: boolean;
  onSuccess?: () => void;
}

// Reference type options
const REFERENCE_TYPE_OPTIONS = [
  { value: 'drawing', label: 'Drawing' },
  { value: 'specification', label: 'Specification' },
  { value: 'photo', label: 'Photo' },
  { value: 'document', label: 'Document' },
  { value: 'other', label: 'Other' },
];

// Type icons
const TYPE_ICONS: Record<RFIReferenceType, React.ReactNode> = {
  drawing: <FileText className="h-4 w-4" />,
  specification: <File className="h-4 w-4" />,
  photo: <Image className="h-4 w-4" />,
  document: <FileText className="h-4 w-4" />,
  other: <File className="h-4 w-4" />,
};

// Type colors
const TYPE_COLORS: Record<RFIReferenceType, string> = {
  drawing: 'bg-blue-100 text-blue-700',
  specification: 'bg-purple-100 text-purple-700',
  photo: 'bg-green-100 text-green-700',
  document: 'bg-orange-100 text-orange-700',
  other: 'bg-gray-100 text-gray-700',
};

export const RFIReferencesSection: React.FC<RFIReferencesSectionProps> = ({
  projectId,
  rfi,
  canEdit,
  onSuccess,
}) => {
  const { success, error: showError } = useToast();

  const [showAddModal, setShowAddModal] = useState(false);
  const [newReference, setNewReference] = useState({
    type: 'drawing' as RFIReferenceType,
    name: '',
    number: '',
    url: '',
    notes: '',
  });

  const [addReference, { isLoading: isAdding }] = useAddRFIReferenceMutation();
  const [removeReference, { isLoading: isRemoving }] = useRemoveRFIReferenceMutation();

  const isLoading = isAdding || isRemoving;

  // Handle add reference
  const handleAddReference = async () => {
    if (!newReference.name.trim()) {
      showError('Reference name is required');
      return;
    }

    try {
      await addReference({
        projectId,
        rfiId: rfi._id,
        data: {
          type: newReference.type,
          name: newReference.name.trim(),
          number: newReference.number.trim() || undefined,
          url: newReference.url.trim() || undefined,
          notes: newReference.notes.trim() || undefined,
        },
      }).unwrap();

      success('Reference added');
      setShowAddModal(false);
      setNewReference({
        type: 'drawing',
        name: '',
        number: '',
        url: '',
        notes: '',
      });
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to add reference');
    }
  };

  // Handle remove reference
  const handleRemoveReference = async (referenceId: string) => {
    if (!window.confirm('Remove this reference?')) return;

    try {
      await removeReference({
        projectId,
        rfiId: rfi._id,
        referenceId,
      }).unwrap();

      success('Reference removed');
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to remove reference');
    }
  };

  const references = rfi.references || [];

  return (
    <div className="space-y-4">
      {/* References List */}
      {references.length > 0 ? (
        <div className="space-y-2">
          {references.map((ref: RFIReference) => (
            <div
              key={ref._id}
              className="flex items-start justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-start gap-3">
                {/* Type Icon */}
                <div className={`p-2 rounded-lg ${TYPE_COLORS[ref.type]}`}>
                  {TYPE_ICONS[ref.type]}
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{ref.name}</p>
                    {ref.number && (
                      <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                        {ref.number}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 capitalize">{ref.type}</p>
                  {ref.notes && <p className="text-xs text-gray-500 mt-1">{ref.notes}</p>}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {ref.url && (
                  <a
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-gray-400 hover:text-brand-600 rounded transition-colors"
                    title="Open"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemoveReference(ref._id || '')}
                    disabled={isLoading}
                    className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-4 text-center">
          <FileText className="h-6 w-6 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No references linked</p>
        </div>
      )}

      {/* Add Button */}
      {canEdit && (
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="w-full py-2 px-4 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Reference</span>
        </button>
      )}

      {/* Add Reference Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowAddModal(false);
              setNewReference({
                type: 'drawing',
                name: '',
                number: '',
                url: '',
                notes: '',
              });
            }}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Add Reference</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setNewReference({
                    type: 'drawing',
                    name: '',
                    number: '',
                    url: '',
                    notes: '',
                  });
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <Dropdown
                label="Type"
                options={REFERENCE_TYPE_OPTIONS}
                value={newReference.type}
                onChange={value =>
                  setNewReference(prev => ({ ...prev, type: value as RFIReferenceType }))
                }
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Name"
                  placeholder="e.g., Floor Plan A"
                  value={newReference.name}
                  onChange={e => setNewReference(prev => ({ ...prev, name: e.target.value }))}
                  required
                />

                <Input
                  label="Number"
                  placeholder="e.g., A-101"
                  value={newReference.number}
                  onChange={e => setNewReference(prev => ({ ...prev, number: e.target.value }))}
                />
              </div>

              <Input
                label="URL"
                placeholder="https://..."
                value={newReference.url}
                onChange={e => setNewReference(prev => ({ ...prev, url: e.target.value }))}
              />

              <TextArea
                label="Notes"
                placeholder="Additional notes about this reference..."
                rows={2}
                value={newReference.notes}
                onChange={value => setNewReference(prev => ({ ...prev, notes: value }))}
              />

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewReference({
                      type: 'drawing',
                      name: '',
                      number: '',
                      url: '',
                      notes: '',
                    });
                  }}
                  disabled={isAdding}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddReference}
                  disabled={!newReference.name.trim() || isAdding}
                  className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {isAdding ? 'Adding...' : 'Add Reference'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RFIReferencesSection;
